import { Observable } from "rxjs";

interface IBuffer {
    buffer: string;
    finishedLines?: string;
}

/*
 * collate(stream)
 *
 * Translate a stream of chunks of text into a stream of arrays of lines of text.
 * Does not include any text after the last newline, so ensure the input is
 * newline-terminated as well as delimited.
 */
export function collate(stream) {
    return stream
        .scan(
            (state: IBuffer, data: string) => {
                const index = data.lastIndexOf("\n");
                if (index >= 0) {
                    return {
                        finishedLines: state.buffer + data.substring(0, index + 1),
                        buffer: data.substring(index + 1),
                    };
                } else {
                    return { buffer: state.buffer + data };
                }
            },
            { buffer: "" }
        )
        .filter((x: IBuffer) => x.finishedLines)
        .map((x: IBuffer) => x.finishedLines.split("\n").filter((finishedLine) => finishedLine.length > 0));
}

/*
 * extractStream (xhr, [options])
 *
 * Listen to download progress events on an XMLHttpRequest and provide the
 * response text in chunks as it is downloaded.  Options can contain the
 * flag "endWithNewline: true" which adds a trailing newline if one did not
 * exist in the source.
 */
export function extractStream(xhr) {
    return Observable.create((observer) => {
        let charactersSeen = 0;

        function notified() {
            if (xhr.readyState >= 3 && xhr.responseText.length > charactersSeen) {
                const chunk = xhr.responseText.substring(charactersSeen);
                observer.next(chunk);
                charactersSeen = xhr.responseText.length;
            }
            if (xhr.readyState === 4) {
                if (xhr.responseText[xhr.responseText.length - 1] !== "\n") {
                    observer.next("\n");
                }
                observer.complete();
            }
        }
        xhr.onreadystatechange = notified;
        xhr.onprogress = notified;
        xhr.onerror = (event) => {
            observer.error(event);
        };
    });
}

interface IOptions {
    xhrFactory?: (url: string, options: IOptions) => XMLHttpRequest;
    beforeOpen?: (xhr: XMLHttpRequest) => void;
    postData?: any;
    method?: "GET" | "POST" | "PUT" | "PATCH";
}

/*
 * stream (url, options)
 *
 * Download line-delimited JSON from specified URL and deliver as an Observable.
 * Options are: method - defaults to GET, postData - default none,
 * xhrFactory - function to supply the XMLHttpRequest used (defaults to
 * new XMLHttpRequest), and beforeOpen - function called before xhr.open() to
 * allow for user-specified customization of the request.
 */
export function stream(url, options: IOptions = {}) {
    const xhr = options.xhrFactory ? options.xhrFactory(url, options) : new XMLHttpRequest();

    const textStream = extractStream(xhr);
    const jsonStream = collate(textStream)
        .concatMap((lineArray) => Observable.from(lineArray)) // replace array of items with sequence
        .map(JSON.parse);

    if (options.beforeOpen) {
        options.beforeOpen(xhr);
    }

    xhr.open(options.method ? options.method : "GET", url);
    xhr.send(options.postData ? options.postData : null);

    return jsonStream;
}
